import BusinessIcon from "@mui/icons-material/Business";
import DoneIcon from "@mui/icons-material/Done";
import { Box, CircularProgress, Typography, LinearProgress, Fade, Paper } from "@mui/material";
import { keyframes } from "@mui/system";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface LoadingPageProps {
  whatToSay: string;
  progress?: number; // Optional progress percentage (0-100)
}

// Define animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingPage: React.FC<LoadingPageProps> = ({ whatToSay, progress }) => {
  const [dots, setDots] = useState(".");
  const { t } = useTranslation();
  const [tips, setTips] = useState<string[]>([
    t("onboarding.organizingBusinessInfo"),
    t("onboarding.finalizingProfile"),
    t("onboarding.settingUpServices"),
    t("onboarding.gettingReady"),
    t("onboarding.almostThere"),
  ]);
  const [currentTip, setCurrentTip] = useState(0);
  const [completed, setCompleted] = useState<string[]>([]);

  // Animate loading dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "." : prev + "."));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Rotate through tips
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTip((prev) => (prev + 1) % tips.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [tips.length]);

  // Simulate tasks being completed
  useEffect(() => {
    const tasks = [
      t("onboarding.connectingToServers"),
      t("onboarding.loadingBusinessProfile"),
      t("onboarding.preparingDashboard"),
      t("onboarding.syncingCalendarData"),
    ];

    const completeTask = (index: number) => {
      if (index < tasks.length) {
        setCompleted((prev) => [...prev, tasks[index]]);
        setTimeout(() => completeTask(index + 1), Math.random() * 2000 + 1000);
      }
    };

    setTimeout(() => completeTask(0), 1000);
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated background elements */}
      {[...Array(5)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            width: "100px",
            height: "100px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `${float} ${Math.random() * 4 + 3}s infinite ease-in-out`,
            zIndex: 0,
          }}
        />
      ))}

      {/* Main content */}
      <Paper
        elevation={24}
        sx={{
          padding: 4,
          borderRadius: 4,
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          backdropFilter: "blur(10px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          maxWidth: "80%",
          width: "500px",
          position: "relative",
          zIndex: 1,
          animation: `${pulse} 3s infinite ease-in-out`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 4,
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: 120,
              height: 120,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress
              size={120}
              thickness={2}
              sx={{
                color: "#64B5F6",
                position: "absolute",
                animation: `${rotate} 8s linear infinite`,
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                borderRadius: "50%",
                width: 100,
                height: 100,
              }}
            >
              <BusinessIcon
                sx={{
                  fontSize: 50,
                  color: "#fff",
                  animation: `${pulse} 2s infinite ease-in-out`,
                }}
              />
            </Box>
          </Box>
        </Box>

        <Typography
          variant="h4"
          sx={{
            color: "#fff",
            fontWeight: "bold",
            textAlign: "center",
            mb: 2,
            textShadow: "0px 2px 4px rgba(0,0,0,0.3)",
          }}
        >
          {whatToSay}
          <span>{dots}</span>
        </Typography>

        <Fade in={true} timeout={1000}>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.8)",
              textAlign: "center",
              mb: 3,
              fontStyle: "italic",
            }}
          >
            {tips[currentTip]}
          </Typography>
        </Fade>

        {progress !== undefined ? (
          <Box sx={{ width: "100%", mb: 3 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  background: "linear-gradient(90deg, #64B5F6 0%, #2196F3 100%)",
                },
              }}
            />
            <Typography variant="body2" sx={{ color: "#fff", textAlign: "center", mt: 1 }}>
              {Math.round(progress)}% Complete
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: "100%", mb: 3 }}>
            <LinearProgress
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 5,
                  background: "linear-gradient(90deg, #64B5F6 0%, #2196F3 100%)",
                },
              }}
            />
          </Box>
        )}

        {/* Completed tasks list */}
        <Box sx={{ width: "100%" }}>
          {completed.map((task, index) => (
            <Fade in={true} key={index} timeout={500}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                  p: 1,
                  borderRadius: 2,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
              >
                <DoneIcon sx={{ color: "#4CAF50", mr: 1 }} />
                <Typography variant="body2" sx={{ color: "#fff" }}>
                  {task}
                </Typography>
              </Box>
            </Fade>
          ))}
        </Box>
      </Paper>

      <Typography
        variant="body2"
        sx={{
          color: "rgba(255, 255, 255, 0.6)",
          textAlign: "center",
          mt: 4,
          position: "absolute",
          bottom: "20px",
        }}
      >
        Thanks for your patience. We are setting everything up for you!
      </Typography>
    </Box>
  );
};

export default LoadingPage;
